<template>
  <v-slide-y-transition group hide-on-leave tag="div" class="height-100">
    <!-- 페이지1: 폼화면 -->
    <div class="pt-8 px-5 color--nice-black height-100" v-if="ready && !showLoadingPage" key="form">
      <!-- 설명 영역 -->
      <div class="mb-4">
        <h1 class="font--m-1">우리동네 크라우드 펀딩<br />한도조회</h1>
        <h4 class="color--nice-grey mb-4">
          여신금융협회 계정정보를 입력하면 실시간 매출 기반 한도를 보여드립니다.
        </h4>

        <h3>
          우리동네 크라우드 펀딩은 번개입금과 중소벤처기업부X소상공인시장진흥공단이 함께 진행하는
          크라우드 펀딩입니다.
        </h3>
      </div>

      <!-- 폼 영역 -->
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(submit)">
          <div
            class="account-group mb-6"
            v-for="(account, index) in accounts"
            :key="account.provider"
          >
            <!-- 플랫폼 이름 -->
            <v-list-item class="px-0 mb-1">
              <v-list-item-avatar tile>
                <v-img :src="account.image"></v-img>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font--m-3">
                  {{ account.name }} ({{ account.required ? '필수' : '선택' }})
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- 아이디 -->
            <ValidationProvider
              :name="`${account.name} 아이디`"
              v-slot="{ errors }"
              :rules="account.required ? 'required' : ''"
              tag="div"
              class="form-control"
            >
              <v-text-field
                v-model="account.userId"
                dense
                outlined
                clearable
                hide-details
                :disabled="loading"
                autocomplete="username"
                :tabindex="index * 2 + 1"
                :error="errors.length > 0"
                :label="`${account.name} 아이디`"
              ></v-text-field>
              <v-slide-y-transition>
                <p v-if="errors.length > 0" class="guide-error">{{ errors[0] }}</p>
              </v-slide-y-transition>
            </ValidationProvider>

            <!-- 비밀번호 -->
            <ValidationProvider
              :name="`${account.name} 비밀번호`"
              v-slot="{ errors }"
              :rules="account.required || account.userId ? 'required' : ''"
              tag="div"
              class="form-control"
            >
              <v-text-field
                v-model="account.password"
                dense
                outlined
                hide-details
                :disabled="loading"
                :tabindex="index * 2 + 2"
                :error="errors.length > 0"
                autocomplete="current-password"
                :label="`${account.name} 비밀번호`"
                :type="account.isShowPassword ? 'text' : 'password'"
              >
                <template v-slot:append>
                  <div @click="account.isShowPassword = !account.isShowPassword">
                    <v-fade-transition group hide-on-leave>
                      <img
                        v-if="!account.isShowPassword"
                        src="@/assets/images/icons/eye-close.svg"
                        key="close"
                      />
                      <img v-else src="@/assets/images/icons/eye-open.svg" key="open" />
                    </v-fade-transition>
                  </div>
                </template>
              </v-text-field>
              <v-slide-y-transition>
                <p v-if="errors.length > 0" class="guide-error">{{ errors[0] }}</p>
              </v-slide-y-transition>
            </ValidationProvider>
          </div>

          <!-- 하단 영역 -->
          <v-layout class="pt-4" column>
            <v-btn
              color="primary"
              block
              large
              depressed
              height="52"
              class="font--m-3"
              :loading="loading"
              :disabled="loading"
              type="submit"
            >
              한도 조회하기
            </v-btn>

            <div class="mt-8 text-center">
              <p class="mb-1">아직 여신협회 아이디가 없다면?</p>
              <v-btn
                color="#0b3269"
                dark
                large
                href="https://m.cardsales.or.kr/page/member/join/joinForm"
              >
                여신협회 바로가기
              </v-btn>
            </div>
          </v-layout>
        </v-form>
      </ValidationObserver>
    </div>

    <!-- 페이지2: 로딩화면 -->
    <div
      class="pt-8 px-5 color--nice-black height-100"
      v-else-if="ready && loading && showLoadingPage"
      key="loading"
    >
      <Loading />
    </div>
    <div style="height: 300px" key="test1"></div>
  </v-slide-y-transition>
</template>

<script>
import { mapGetters } from 'vuex'
import { defaultApi } from '@/plugins/api/defaultApi'

export default {
  props: ['amount'],
  components: {
    Loading: () => import('@/pages/landing/woori-crowd/limit-inquiry/Loading.vue'),
  },
  data() {
    return {
      showLoadingPageTimeout: null,
      showLoadingPage: false,
      accounts: [
        {
          provider: 'cfa',
          name: '여신금융협회',
          userId: '',
          password: '',
          isShowPassword: false,
          title: '정확한 선정산을 위해<br />여신금융협회 계정이 필요해요!',
          required: true,
          image: require('@/assets/images/accounts/cfa-logo.png'),
        },
        // {
        //   provider: 'baemin',
        //   name: '배달의 민족',
        //   userId: '',
        //   password: '',
        //   isShowPassword: false,
        //   title: '배달 매출 선정산도 원하신다면<br />계정정보를 입력해 주세요!',
        //   image: require('@/assets/images/accounts/baemin-logo.png'),
        // },
      ],
      ready: false,
      loading: false,
    }
  },
  watch: {
    async loading(v) {
      if (this.showLoadingPageTimeout) {
        clearTimeout(this.showLoadingPageTimeout)
        this.showLoadingPageTimeout = null
        this.showLoadingPage = false
      }

      if (v) {
        this.showLoadingPageTimeout = setTimeout(() => {
          this.showLoadingPage = true
        }, 1000)
      }
    },
  },
  computed: {
    ...mapGetters('auth', ['referralCode']),

    submitForm() {
      const form = {}
      for (const account of this.accounts) {
        const { userId, password } = account
        if (userId && password) {
          form[`${account.provider}Credentials`] = { userId, password }
        }
      }

      if (this.referralCode) {
        form.referralCode = this.referralCode
      }

      return form
    },
  },
  mounted() {
    this.ready = true
  },
  methods: {
    async submit() {
      if (this.loading) {
        return
      }
      try {
        this.loading = true
        const {
          data: { amount, uuid },
        } = await defaultApi.postCrowdFundingReview(this.submitForm)
        this.$emit('update:amount', amount)
        this.$emit('update:uuid', uuid)
      } catch (e) {
        let message = '한도 조회에 실패했습니다. 문제가 지속될 경우 02-6925-1788로 문의해주세요.'
        if (typeof e?.message === 'object') {
          const errors = Object.keys(e?.message)
          if (errors.length > 0) {
            message = e?.message[errors[0]][0]
          }
        }
        this.$swal.basic.alert({
          html: message,
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.account-group {
  .form-control:not(:last-of-type) {
    margin-bottom: 12px;
  }

  &:not(:last-of-type) {
    margin-bottom: 24px;
  }

  .guide-error {
    margin-top: 2px;
    font-size: 13px;
    color: #ff1616;
    font-weight: 900;
  }

  ::v-deep .v-input__append-inner img {
    cursor: pointer;
  }
}
</style>
